import { createContext, useContext } from 'react';

export type ModalStackActions = {
  /** Function to push a new modal to the top of the stack */
  push: (props: NewModalProps) => Modal;
  /**
   * Function to pop a single modal from the stack
   *
   * If the `modal` parameter is specified, this will pop all modals up through the specified modal.
   * If no `modal` parameter is specified, this will pop the modal at the top of the stack.
   * */
  pop: (modal?: Modal | string) => void;
  /** Function to pop all modals from the modal stack */
  popAll: () => void;
  /** Shortcut function to push a new modal to the top of the stack to ask user to confirm something */
  pushConfirm: (props: PushConfirmProps) => Modal;
  /** Shortcut function to push a new modal to the top of the stack to alert the user of something */
  pushAlert: (props: PushAlertProps) => Modal;
};

export type ModalStackState = {
  /** Modals currently in the stack */
  modals: Modal[];
  /** Number of modals currently in the stack */
  size: number;
  /** Whether there are any modals currently in the stack */
  empty: boolean;
};

export interface PushConfirmProps extends StandardModalContent {
  /** Function to run when the user either confirms or cancels */
  onClose: (confirmed: boolean) => any | Promise<any>;
  /** Whether the modal should close when the modal background is clicked  */
  closeOnBackgroundClick?: boolean;
}

export interface PushAlertProps extends StandardModalContent {
  /** Function to run when the user closes the alert */
  onClose?: () => any | Promise<any>;
  /** Whether the modal should close when the modal background is clicked  */
  closeOnBackgroundClick?: boolean;
}

export interface Modal {
  /** ID of the modal */
  id: string;
  /** Index of the modal identifying its location within the modal stack */
  index: number;
  /** JSX content of the modal */
  content: JSX.Element;
}

export interface NewModalProps {
  /** ID for unniquely identifying a modal
   *
   * In most cases, this is unnecessary since each modal will be automatically assigned an `id` if this is left blank.
   */
  id?: string;
  /**
   * Content to be displayed on the modal
   *
   * To display a standard modal, populate the relevant `StandardModalContent` properties.
   * To display an entirely custom modal with custom styling, pass in the custom modal's JSX content.
   * */
  content: StandardModalContent | JSX.Element;
  /** class name to use for the content box */
  className?: string;
  /** Whether the modal should close when the modal background is clicked  */
  closeOnBackgroundClick?: boolean;
}

export type ReactModalButton = React.ReactElement<
  React.ButtonHTMLAttributes<HTMLButtonElement>
>;

export interface StandardModalContent {
  /** Body text of the modal */
  body: string | JSX.Element;
  /** Title to appear at the top of the modal */
  title?: string;
  /**
   * Content of the OK button
   *
   * If `null`, no OK button will be displayed
   */
  okButton?: string | ReactModalButton | null;
  /**
   * Content of the Cancel button
   *
   * If `null`, no Cancel button will be displayed
   */
  cancelButton?: string | ReactModalButton | null;
  /**
   * Callback function to run whenever the OK button is clicked
   *
   * If the `content` parameter contains input fields, they can be put into an object and used as a parameter in the callback
   * */
  onOk?: (inputValues?: EnteredInputs) => any | Promise<any>;
  /** Callback function to run whenever the Cancel button is clicked */
  onCancel?: () => any | Promise<any>;
  /** Array of inputs to show on the modal */
  inputs?: ModalInput[];
  /**
   * Whether an 'X' should appear on the modal to close it
   *
   * If unspecified, will default to `false`. It will also not display if the `title` parameter is undefined.
   * */
  showCloseX?: boolean;
}

export interface ModalInput {
  /** ID of the input which will designate it within the `inputValues` object */
  id: string;
  /** Existing value of the input (if any) */
  value?: string;
  /** Label describing the input */
  label?: string;
  /**
   * Type of input being entered
   *
   * Note: If no `rows` param is passed within TextInputParams, the input will be a simple text input. If a `rows` param is passed in, the input will be a textarea input.
   *  */
  params: DropdownSelectParams | TextInputParams;
  /** Whether or not the input is required for clicking the OK button */
  required: boolean;
}

interface TextInputParams {
  /** Max number of characters allowed */
  maxLength?: number;
  /** Number of rows' height */
  rows?: number;
}

interface DropdownSelectParams {
  /** Dropdown options */
  options: {
    value: string;
    label: string;
  }[];
}

export interface EnteredInputs {
  /** Input IDs mapped to the user-entered value for that input */
  [inputId: string]: string | null;
}

export const ModalStackActionsContext = createContext<
  ModalStackActions | undefined
>(undefined);
export const ModalStackStateContext = createContext<{
  modals: Modal[];
  size: number;
  empty: boolean;
}>({ modals: [], size: 0, empty: true });

export function useModalStackActions() {
  return useContext(ModalStackActionsContext);
}

export function useModalStackState() {
  return useContext(ModalStackStateContext);
}
